/**
 * Utility module for managing avatar sharing state in localStorage
 * This provides a clean API for storing and retrieving sharing-related data
 * with proper error handling and type safety
 */

const STORAGE_KEYS = {
  CLIENT_ID: "clientId",
  SESSION_ID: "sessionId",
  ORG_NAME: "orgName",
  SHARE_CODE: "shareCode",
  REDIRECT_URL: "redirectUrl",
  DEEPLINK_FLAG: "deeplink",
  SESSION_TIMESTAMP: "sharingSessionTimestamp",
  // The scan the current share was used for (BUG-0012 keep-until-approved).
  SHARED_SCAN_ID: "sharedScanId",
} as const;

/**
 * DOM event dispatched when the widget sessionId is written (see
 * setAvatarSharingData). src/Utils/hotjar.ts listens for it to tag the active
 * Hotjar recording with the session id. The constant lives HERE, not in
 * hotjar.ts, so this module needs no import from hotjar: hotjar already imports
 * getAvatarSharingData from here, and a back-import would form a circular
 * dependency. See HERE_BE_DRAGONS.md.
 */
export const SHARING_UPDATED_EVENT = "ttf:sharing-updated";

// Session expires after 2 hours (in milliseconds)
const SESSION_EXPIRY_MS = 2 * 60 * 60 * 1000;

// Special clientIds for Malchus, Neems, StyleMatch and Sisley environments (never expire)
const MALCHUS_CLIENT_ID = "715053";
const NEEMS_CLIENT_ID = "553777";
const SISLEY_CLIENT_ID = "417017";
// Sisley's parent account (417017) has SUB-ACCOUNTS in the dashboard's
// parent/sub hierarchy; 454934 is the first (jack@sisleyclothing.com.au). Every
// sub opens this same branded Sisley build, so the send-avatar gate and env-init
// must accept it. This list cannot be derived at runtime -- `hierarchy_members`
// is server-only (default-deny in firestore.rules) and this is a client bundle
// -- so each new Sisley sub must be added HERE by hand, and to the host maps in
// bodyscanner_dashboard + truetoform-api. Same shape as StyleMatch below.
const SISLEY_SUB_CLIENT_IDS = ["454934"];
const SISLEY_CLIENT_IDS = [SISLEY_CLIENT_ID, ...SISLEY_SUB_CLIENT_IDS];
const STYLEMATCH_CLIENT_ID = "809856";
// StyleMatch runs a SECOND clientId on our prod for the tenant's staging store;
// both open the same branded StyleMatch PWA. 809856 stays the walk-in default,
// while a deep-link session keeps whichever StyleMatch clientId it arrived with.
const STYLEMATCH_STAGING_CLIENT_ID = "542101";
const STYLEMATCH_CLIENT_IDS = [
  STYLEMATCH_CLIENT_ID,
  STYLEMATCH_STAGING_CLIENT_ID,
];

// Environment-based (white-label tenant) clientIds are permanent: they represent
// the deployment itself, not a share session, so they never expire and are never
// cleared. See MALCHUS_NEEMS_PROTECTION.md.
const ENVIRONMENT_CLIENT_IDS: string[] = [
  MALCHUS_CLIENT_ID,
  NEEMS_CLIENT_ID,
  ...SISLEY_CLIENT_IDS,
  ...STYLEMATCH_CLIENT_IDS,
];

const isEnvironmentClientId = (clientId: string | null): boolean =>
  clientId !== null && ENVIRONMENT_CLIENT_IDS.includes(clientId);

// Both StyleMatch clientIds (prod 809856 + staging 542101) resolve to the same
// branded PWA build, so the send-avatar gate and env-init must accept either.
export const isStyleMatchClientId = (clientId: string | null): boolean =>
  clientId !== null && STYLEMATCH_CLIENT_IDS.includes(clientId);

// The Sisley parent clientId and every linked sub-account resolve to the same
// branded PWA build, so the send-avatar gate and env-init must accept any of
// them. 417017 stays the walk-in default.
export const isSisleyClientId = (clientId: string | null): boolean =>
  clientId !== null && SISLEY_CLIENT_IDS.includes(clientId);

export interface AvatarSharingData {
  clientId: string | null;
  sessionId: string | null;
  orgName: string | null;
  shareCode: string | null;
  redirectUrl?: string | null;
  timestamp?: number | null;
}

/**
 * Stores avatar sharing data in localStorage
 * @param data Partial sharing data to store
 */
export const setAvatarSharingData = (
  data: Partial<AvatarSharingData>
): void => {
  try {
    // Set timestamp when storing sharing data. Storing a clientId starts a
    // share (the /send-avatar landing, a typed share code, AvatarSelect's New
    // Avatar), and a share that has just started has not been used for a scan
    // yet — so the scan an earlier share was used for is forgotten (BUG-0012).
    // Otherwise that scan's approval would end this new share the moment the
    // next form opens.
    if (data.clientId !== undefined && data.clientId !== null) {
      localStorage.setItem(
        STORAGE_KEYS.SESSION_TIMESTAMP,
        Date.now().toString()
      );
      localStorage.removeItem(STORAGE_KEYS.SHARED_SCAN_ID);
    }

    if (data.clientId !== undefined) {
      if (data.clientId === null) {
        localStorage.removeItem(STORAGE_KEYS.CLIENT_ID);
      } else {
        localStorage.setItem(STORAGE_KEYS.CLIENT_ID, data.clientId);
      }
    }

    if (data.sessionId !== undefined) {
      if (data.sessionId === null) {
        localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
      } else {
        localStorage.setItem(STORAGE_KEYS.SESSION_ID, data.sessionId);
        // Let Hotjar tagging (src/Utils/hotjar.ts) know the widget sessionId is
        // now available. Best-effort and isolated so a listener/dispatch failure
        // never corrupts the storage-write success path (or mislabels it in the
        // outer catch below).
        try {
          window.dispatchEvent(new Event(SHARING_UPDATED_EVENT));
        } catch {
          /* no-op */
        }
      }
    }

    if (data.orgName !== undefined) {
      if (data.orgName === null) {
        localStorage.removeItem(STORAGE_KEYS.ORG_NAME);
      } else {
        localStorage.setItem(STORAGE_KEYS.ORG_NAME, data.orgName);
      }
    }

    if (data.shareCode !== undefined) {
      if (data.shareCode === null) {
        localStorage.removeItem(STORAGE_KEYS.SHARE_CODE);
      } else {
        localStorage.setItem(STORAGE_KEYS.SHARE_CODE, data.shareCode);
      }
    }

    if (data.redirectUrl !== undefined) {
      if (data.redirectUrl === null) {
        localStorage.removeItem(STORAGE_KEYS.REDIRECT_URL);
      } else {
        localStorage.setItem(STORAGE_KEYS.REDIRECT_URL, data.redirectUrl);
      }
    }
  } catch (error) {
    console.error("Error storing avatar sharing data:", error);
  }
};

/**
 * Retrieves avatar sharing data from localStorage
 * @returns Object containing all sharing-related data
 */
export const getAvatarSharingData = (): AvatarSharingData => {
  try {
    const timestampStr = localStorage.getItem(STORAGE_KEYS.SESSION_TIMESTAMP);
    const timestamp = timestampStr ? parseInt(timestampStr, 10) : null;

    return {
      clientId: localStorage.getItem(STORAGE_KEYS.CLIENT_ID),
      sessionId: localStorage.getItem(STORAGE_KEYS.SESSION_ID),
      orgName: localStorage.getItem(STORAGE_KEYS.ORG_NAME),
      shareCode: localStorage.getItem(STORAGE_KEYS.SHARE_CODE),
      redirectUrl: localStorage.getItem(STORAGE_KEYS.REDIRECT_URL),
      timestamp,
    };
  } catch (error) {
    console.error("Error retrieving avatar sharing data:", error);
    return {
      clientId: null,
      sessionId: null,
      orgName: null,
      shareCode: null,
      redirectUrl: null,
      timestamp: null,
    };
  }
};

/**
 * Checks if the sharing session has expired
 * @returns true if session is expired or invalid
 * Note: Malchus and Neems clientIds never expire (environment-based)
 */
export const isSharingSessionExpired = (): boolean => {
  try {
    // Check if this is an environment-based clientId (never expire)
    const clientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
    if (isEnvironmentClientId(clientId)) {
      return false; // Environment-based clientIds never expire
    }

    const timestampStr = localStorage.getItem(STORAGE_KEYS.SESSION_TIMESTAMP);
    if (!timestampStr) return true; // No timestamp = expired

    const timestamp = parseInt(timestampStr, 10);
    if (isNaN(timestamp)) return true; // Invalid timestamp = expired

    const now = Date.now();
    const age = now - timestamp;

    return age > SESSION_EXPIRY_MS; // Expired if older than 2 hours
  } catch (error) {
    console.error("Error checking session expiry:", error);
    return true; // Assume expired on error
  }
};

/**
 * Checks if there is active avatar sharing data (and not expired)
 * @returns true if clientId exists and session is not expired
 *
 * The 2-hour window is the ONLY thing that ends a share here. Until BUG-0012
 * this also cleared the share whenever the per-tab `deeplink` flag was
 * missing, reading that as "the browser was closed and reopened". But the OS
 * discards backgrounded pages and the browser rebuilds them with localStorage
 * intact and sessionStorage empty — the same signature — so shoppers who
 * stepped away mid-scan silently lost their client (18 of 2,221 share-link
 * visits, 2026-08-22 → 09-11, 17 of them widget shares). The case that check
 * protected — someone reopens the app later and scans without meaning to
 * share — was never observed; the form's "Sending Avatar to:" banner and its
 * Cancel cover it now. Do not reintroduce a sessionStorage test here.
 */
export const hasActiveSharingSession = (): boolean => {
  try {
    const clientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
    if (!clientId || clientId.length === 0) return false;

    // Check if session is expired
    if (isSharingSessionExpired()) {
      // Auto-cleanup expired session
      clearAvatarSharingData();
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking sharing session:", error);
    return false;
  }
};

/**
 * Records the scan a share was used for (ScanModel.create, on a shared
 * submit). The share is no longer cleared on submit (BUG-0012): it stays until
 * THIS scan is approved, so a redo after a failed avatar reaches the same
 * client. That decision is made when the next scan form opens — see
 * `src/Utils/sharedScanApproval.ts`. Starting a share again
 * (`setAvatarSharingData` with a clientId) forgets it: a re-opened link is a
 * share that has not been used yet.
 */
export const setSharedScanId = (scanId: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.SHARED_SCAN_ID, scanId);
  } catch (error) {
    console.error("Error storing shared scan id:", error);
  }
};

/** The scan the current share was used for, or null if it has not been used. */
export const getSharedScanId = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.SHARED_SCAN_ID);
  } catch (error) {
    console.error("Error reading shared scan id:", error);
    return null;
  }
};

/**
 * The interim label for a share whose client name is not known yet. Every
 * screen that asks "who is this going to?" shows this rather than a blank
 * while the name is still resolving — or permanently, if it never does.
 */
export const UNRESOLVED_RECIPIENT_LABEL = "Selected Org";

/**
 * Who a share is going to, for display — never blank while there is a
 * client. The landing's name lookup can time out (BUG-0011's budget), which
 * left "Sending Avatar to:" empty; with the browser-close guard gone, that
 * banner is how a shopper learns their scan will be shared.
 *
 * `""` — and only the no-client case — is load-bearing: callers hide the
 * banner on it. Naming a recipient that does not exist would tell a shopper
 * their scan is being shared when it is not.
 *
 * This is display only. The recipient is always the deep link's clientId, and
 * `ScanModel.create` writes it whether or not the name ever resolved, so a
 * scan shown as "Selected Org" still reaches the right client.
 *
 * `[2.5.20]` used `share code <clientId>` here instead — a real identifier a
 * shopper could cross-check. It was replaced because only this one screen had
 * it: the two screens a timed-out landing actually reaches showed a blank or
 * no banner at all, so one flow labelled the same unknown client three
 * different ways. One word everywhere beat a better word in one place.
 */
export const shareRecipientLabel = (
  data: Pick<AvatarSharingData, "orgName" | "clientId">,
): string =>
  data.orgName?.trim() || (data.clientId ? UNRESOLVED_RECIPIENT_LABEL : "");

/**
 * Clears all avatar sharing data from localStorage
 * Note: Preserves Malchus and Neems clientIds (environment-based, never cleared)
 */
export const clearAvatarSharingData = (): void => {
  try {
    // Check if this is an environment-based tenant clientId
    const clientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
    const isEnvironmentBased = isEnvironmentClientId(clientId);

    // Only clear clientId if it's NOT an environment-based tenant
    if (!isEnvironmentBased) {
      localStorage.removeItem(STORAGE_KEYS.CLIENT_ID);
      localStorage.removeItem(STORAGE_KEYS.ORG_NAME);
    }
    // Note: We preserve clientId and orgName for Malchus/Neems

    // Always clear session-specific data
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.SHARE_CODE);
    localStorage.removeItem(STORAGE_KEYS.REDIRECT_URL);
    localStorage.removeItem(STORAGE_KEYS.SESSION_TIMESTAMP);
    localStorage.removeItem(STORAGE_KEYS.SHARED_SCAN_ID);
    localStorage.removeItem(STORAGE_KEYS.DEEPLINK_FLAG);
    sessionStorage.removeItem(STORAGE_KEYS.DEEPLINK_FLAG);
  } catch (error) {
    console.error("Error clearing avatar sharing data:", error);
  }
};

/**
 * Sets the deeplink flag in sessionStorage
 * This is used to track if the user came from a deeplink
 */
export const setDeeplinkFlag = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.DEEPLINK_FLAG, "1");
  } catch (error) {
    console.error("Error setting deeplink flag:", error);
  }
};

/**
 * Checks if the current session is from a deeplink
 */
export const isDeeplinkSession = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.DEEPLINK_FLAG) === "1";
  } catch (error) {
    console.error("Error checking deeplink flag:", error);
    return false;
  }
};

/**
 * Initializes environment-based clientId for Malchus or Neems environments
 * This should be called on app startup to ensure auto-sharing in these environments
 */
export const initializeEnvironmentBasedSharing = (): void => {
  try {
    const isMalchusEnv = process.env.REACT_APP_IS_MALCHUS_ENV === "true";
    const isNeemsEnv = process.env.REACT_APP_IS_NEEMS_ENV === "true";
    const isStyleMatchEnv = process.env.REACT_APP_IS_STYLEMATCH_ENV === "true";
    const isSisleyEnv = process.env.REACT_APP_IS_SISLEY_ENV === "true";

    // Only initialize if in an environment-based (white-label) deployment
    if (!isMalchusEnv && !isNeemsEnv && !isStyleMatchEnv && !isSisleyEnv) {
      return; // Not an environment-based deployment
    }

    // The clientId(s) that belong to THIS white-label build. StyleMatch has two
    // (prod 809856 + staging 542101); Malchus/Neems/Sisley have one each.
    const existingClientId = localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
    const envClientIds = isMalchusEnv
      ? [MALCHUS_CLIENT_ID]
      : isNeemsEnv
      ? [NEEMS_CLIENT_ID]
      : isSisleyEnv
      ? [SISLEY_CLIENT_ID]
      : STYLEMATCH_CLIENT_IDS;
    // Walk-in default when no valid env clientId is present yet (StyleMatch → the
    // prod id 809856). A deep-link session already carrying a valid env clientId
    // (e.g. the 542101 staging id) is KEPT — overwriting it here would
    // mis-attribute staging scans to the prod account.
    const defaultClientId = isMalchusEnv
      ? MALCHUS_CLIENT_ID
      : isNeemsEnv
      ? NEEMS_CLIENT_ID
      : isSisleyEnv
      ? SISLEY_CLIENT_ID
      : STYLEMATCH_CLIENT_ID;

    // Seed the default only when there is no valid clientId for this env yet.
    if (!existingClientId || !envClientIds.includes(existingClientId)) {
      localStorage.setItem(STORAGE_KEYS.CLIENT_ID, defaultClientId);

      // Set appropriate org name (overwritten once fetched from Firestore)
      const orgName = isMalchusEnv
        ? "Malchus"
        : isNeemsEnv
        ? "Neems"
        : isSisleyEnv
        ? "Sisley Clothing Australia"
        : "StyleMatch";
      localStorage.setItem(STORAGE_KEYS.ORG_NAME, orgName);

      console.log(
        `Initialized ${orgName} environment-based sharing with clientId: ${defaultClientId}`
      );
    }
  } catch (error) {
    console.error("Error initializing environment-based sharing:", error);
  }
};
